// YouTube API utilities for Aruba Creative channel

// YouTube API key - replace with your actual API key
// Note: In production, this should be stored in environment variables
const YOUTUBE_API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY || 'AIzaSyBcn2pu427U61O1Kz8kAOcj-D40cTXV0ns';
// Aruba Creative YouTube channel ID
const CHANNEL_ID = process.env.REACT_APP_YOUTUBE_CHANNEL_ID || 'UC9vIj8hcyjBfr6Jk4LhUGhQ'; // Aruba Creative channel ID

/**
 * Fetches playlists from the Aruba Creative YouTube channel
 * @returns {Promise<Array>} - Array of playlist objects
 */
export const fetchYouTubePlaylists = async () => {
  try {
    console.log('Fetching YouTube playlists for channel:', CHANNEL_ID);
    const response = await fetch(
      `https://www.googleapis.com/youtube/v3/playlists?part=snippet&channelId=${CHANNEL_ID}&maxResults=50&key=${YOUTUBE_API_KEY}`
    );
    
    if (!response.ok) {
      const errorData = await response.json();
      console.error('YouTube API error response:', errorData);
      throw new Error(`YouTube API error: ${response.status} - ${errorData.error?.message || 'Unknown error'}`);
    }
    
    const data = await response.json();
    console.log('YouTube playlists fetched:', data);
    
    // Process and return the playlists
    return data.items.map(item => ({
      id: item.id,
      title: item.snippet.title,
      description: item.snippet.description,
      thumbnailUrl: item.snippet.thumbnails.high?.url || item.snippet.thumbnails.default?.url
    }));
  } catch (error) {
    console.error('Error fetching YouTube playlists:', error);
    // Fallback to mock data if API fails
    return MOCK_YOUTUBE_DATA.playlists;
  }
};

/**
 * Fetches videos from a specific YouTube playlist
 * @param {string} playlistId - ID of the playlist to fetch videos from
 * @param {number} maxResults - Maximum number of videos to fetch (default: 9)
 * @returns {Promise<Array>} - Array of video objects
 */
export const fetchPlaylistVideos = async (playlistId, maxResults = 9) => {
  try {
    console.log('Fetching videos for playlist:', playlistId);
    const response = await fetch(
      `https://www.googleapis.com/youtube/v3/playlistItems?part=snippet,contentDetails&playlistId=${playlistId}&maxResults=${maxResults}&key=${YOUTUBE_API_KEY}`
    );
    
    if (!response.ok) {
      const errorData = await response.json();
      console.error('YouTube API error response:', errorData);
      throw new Error(`YouTube API error: ${response.status} - ${errorData.error?.message || 'Unknown error'}`);
    }
    
    const data = await response.json();
    console.log('YouTube videos fetched for playlist:', playlistId, data);
    
    // Process and return the videos
    return data.items.map(item => ({
      id: item.contentDetails.videoId,
      title: item.snippet.title,
      description: item.snippet.description,
      thumbnailUrl: item.snippet.thumbnails.high?.url || item.snippet.thumbnails.medium?.url || item.snippet.thumbnails.default?.url,
      publishedAt: item.snippet.publishedAt,
      playlistId: playlistId
    }));
  } catch (error) {
    console.error(`Error fetching videos for playlist ${playlistId}:`, error);
    // Fallback to mock data if API fails
    return MOCK_YOUTUBE_DATA.videos[playlistId] || [];
  }
};

/**
 * Alternative method to fetch videos directly from channel if playlists don't work
 * @returns {Promise<Array>} - Array of video objects
 */
export const fetchChannelVideos = async (maxResults = 20) => {
  try {
    console.log('Fetching videos directly from channel:', CHANNEL_ID);
    const response = await fetch(
      `https://www.googleapis.com/youtube/v3/search?part=snippet&channelId=${CHANNEL_ID}&maxResults=${maxResults}&order=date&type=video&key=${YOUTUBE_API_KEY}`
    );
    
    if (!response.ok) {
      const errorData = await response.json();
      console.error('YouTube API error response:', errorData);
      throw new Error(`YouTube API error: ${response.status} - ${errorData.error?.message || 'Unknown error'}`);
    }
    
    const data = await response.json();
    console.log('YouTube channel videos fetched:', data);
    
    // Process and return the videos
    return data.items.map(item => ({
      id: item.id.videoId,
      title: item.snippet.title,
      description: item.snippet.description,
      thumbnailUrl: item.snippet.thumbnails.high?.url || item.snippet.thumbnails.medium?.url || item.snippet.thumbnails.default?.url,
      publishedAt: item.snippet.publishedAt,
      channelTitle: item.snippet.channelTitle
    }));
  } catch (error) {
    console.error('Error fetching channel videos:', error);
    // Fallback to mock data
    return MOCK_YOUTUBE_DATA.videos.playlist1;
  }
};

/**
 * Fetches all videos from all playlists in the channel
 * @param {number} maxResultsPerPlaylist - Maximum number of videos to fetch per playlist
 * @returns {Promise<Object>} - Object with playlist IDs as keys and arrays of videos as values
 */
export const fetchAllYoutubeVideos = async (maxResultsPerPlaylist = 9) => {
  try {
    // First fetch all playlists
    const playlists = await fetchYouTubePlaylists();
    
    // Then fetch videos for each playlist
    const videosByPlaylist = {};
    for (const playlist of playlists) {
      const videos = await fetchPlaylistVideos(playlist.id, maxResultsPerPlaylist);
      videosByPlaylist[playlist.id] = videos;
    }
    
    return videosByPlaylist;
  } catch (error) {
    console.error('Error fetching all YouTube videos:', error);
    return MOCK_YOUTUBE_DATA.videos;
  }
};

/**
 * Creates a YouTube video embed URL
 * @param {string} videoId - YouTube video ID
 * @returns {string} - YouTube embed URL
 */
export const getYouTubeEmbedUrl = (videoId) => {
  return `https://www.youtube.com/embed/${videoId}`;
};

/**
 * Creates a YouTube video watch URL
 * @param {string} videoId - YouTube video ID
 * @returns {string} - YouTube watch URL
 */
export const getYouTubeWatchUrl = (videoId) => {
  return `https://www.youtube.com/watch?v=${videoId}`;
};

// Mock data for development/testing when API fails
export const MOCK_YOUTUBE_DATA = {
  playlists: [
    {
      id: 'playlist1',
      title: 'Branding Projects',
      description: 'Our branding work for various clients',
      thumbnailUrl: 'https://via.placeholder.com/480x360'
    },
    {
      id: 'playlist2',
      title: 'Design Projects',
      description: 'Our design work for various clients',
      thumbnailUrl: 'https://via.placeholder.com/480x360'
    },
    {
      id: 'playlist3',
      title: 'Video Projects',
      description: 'Our video work for various clients',
      thumbnailUrl: 'https://via.placeholder.com/480x360'
    }
  ],
  videos: {
    'playlist1': Array(9).fill().map((_, i) => ({
      id: `video${i+1}`,
      title: `Branding Project ${i+1}`,
      description: `Description for branding project ${i+1}`,
      thumbnailUrl: `https://via.placeholder.com/480x360?text=Branding+${i+1}`,
      publishedAt: new Date().toISOString(),
      playlistId: 'playlist1'
    })),
    'playlist2': Array(9).fill().map((_, i) => ({
      id: `video${i+10}`,
      title: `Design Project ${i+1}`,
      description: `Description for design project ${i+1}`,
      thumbnailUrl: `https://via.placeholder.com/480x360?text=Design+${i+1}`,
      publishedAt: new Date().toISOString(),
      playlistId: 'playlist2'
    })),
    'playlist3': Array(9).fill().map((_, i) => ({
      id: `video${i+20}`,
      title: `Video Project ${i+1}`,
      description: `Description for video project ${i+1}`,
      thumbnailUrl: `https://via.placeholder.com/480x360?text=Video+${i+1}`,
      publishedAt: new Date().toISOString(),
      playlistId: 'playlist3'
    }))
  }
};

export default {
  fetchYouTubePlaylists,
  fetchPlaylistVideos,
  fetchChannelVideos,
  fetchAllYoutubeVideos,
  getYouTubeEmbedUrl,
  getYouTubeWatchUrl,
  MOCK_YOUTUBE_DATA
}; 